import React, { useState } from "react";
import { Link } from "react-router-dom";
import MerchantPageShell from "../../components/merchant/MerchantPageShell";
import { submitMerchantApplication } from "../../lib/authApi";

export default function MerchantSignup() {
  const [form, setForm] = useState({
    store_name: "",
    website: "",
    instagram: "",
    contact_name: "",
    email: "",
    location: "",
    ecommerce_platform: "",
    product_count: "",
    retailer_type: "",
    product_categories: "",
    store_point_of_view: "",
    has_front_facing_images: "",
    open_to_early_feedback: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const storeName = form.store_name.trim();
    const website = form.website.trim();
    const contactName = form.contact_name.trim();
    const email = form.email.trim();

    if (!storeName || !website || !contactName || !email) {
      setError("Store name, website, contact name, and email are required.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      await submitMerchantApplication({
        ...form,
        store_name: storeName,
        website,
        contact_name: contactName,
        email,
      });

      setSubmitted(true);
    } catch (err) {
      console.error("Merchant application failed", err);
      setError(err?.message || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <MerchantPageShell
        eyebrow="Merchant Application"
        title="Application received"
        lede="Thank you for applying to partner with Veya."
      >
        <div className="public-card" style={{ maxWidth: 680 }}>
          <p className="public-body" style={{ marginTop: 0 }}>
            Veya is currently reviewing independent boutiques and curated fashion
            retailers for early partner access.
          </p>

          <p className="public-body">
            If your store looks like a good fit, we’ll be in touch with next
            steps, including the merchant agreement and account setup.
          </p>

          <p className="public-body public-muted">
            Submitting an application does not guarantee approval.
          </p>

          <div className="merchant-actions">
            <Link className="gold-btn merchant-inline-btn" to="/">
              Back to Veya
            </Link>

            <Link className="public-navlink" to="/merchant/login">
              Already approved?
            </Link>
          </div>
        </div>
      </MerchantPageShell>
    );
  }

  return (
    <MerchantPageShell
      eyebrow="Merchant Application"
      title="Apply to partner with Veya"
      lede="Veya is built for independent boutiques and curated fashion retailers. Applications are reviewed before merchant accounts are created."
    >
      <form className="merchant-form" onSubmit={handleSubmit}>
        <label className="merchant-field">
          <span className="merchant-label">Store name *</span>
          <input
            className="merchant-input"
            value={form.store_name}
            onChange={(e) => updateField("store_name", e.target.value)}
            placeholder="Atelier Edit"
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Website *</span>
          <input
            className="merchant-input"
            value={form.website}
            onChange={(e) => updateField("website", e.target.value)}
            placeholder="https://yourstore.co.nz"
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Instagram</span>
          <input
            className="merchant-input"
            value={form.instagram}
            onChange={(e) => updateField("instagram", e.target.value)}
            placeholder="@yourstore"
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Contact name *</span>
          <input
            className="merchant-input"
            value={form.contact_name}
            onChange={(e) => updateField("contact_name", e.target.value)}
            placeholder="Your name"
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Email *</span>
          <input
            className="merchant-input"
            type="email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            placeholder="hello@yourstore.com"
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Location</span>
          <input
            className="merchant-input"
            value={form.location}
            onChange={(e) => updateField("location", e.target.value)}
            placeholder="Queenstown, New Zealand"
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Ecommerce platform</span>
          <select
            className="merchant-input"
            value={form.ecommerce_platform}
            onChange={(e) => updateField("ecommerce_platform", e.target.value)}
          >
            <option value="">Select one</option>
            <option value="Shopify">Shopify</option>
            <option value="WooCommerce">WooCommerce</option>
            <option value="Squarespace">Squarespace</option>
            <option value="Wix">Wix</option>
            <option value="Other">Other</option>
            <option value="Not sure">Not sure</option>
          </select>
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Approximate product count</span>
          <select
            className="merchant-input"
            value={form.product_count}
            onChange={(e) => updateField("product_count", e.target.value)}
          >
            <option value="">Select one</option>
            <option value="Under 50">Under 50</option>
            <option value="50-200">50–200</option>
            <option value="200-500">200–500</option>
            <option value="500+">500+</option>
          </select>
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Retailer type</span>
          <select
            className="merchant-input"
            value={form.retailer_type}
            onChange={(e) => updateField("retailer_type", e.target.value)}
          >
            <option value="">Select one</option>
            <option value="Independent boutique">Independent boutique</option>
            <option value="Independent designer / own label">
              Independent designer / own label
            </option>
            <option value="Multi-brand fashion retailer">
              Multi-brand fashion retailer
            </option>
            <option value="Marketplace / reseller">Marketplace / reseller</option>
            <option value="Other">Other</option>
          </select>
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Product categories</span>
          <textarea
            className="merchant-input merchant-textarea merchant-textarea--compact"
            value={form.product_categories}
            onChange={(e) => updateField("product_categories", e.target.value)}
            placeholder="Dresses, knitwear, denim, occasionwear, accessories..."
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Store point of view</span>
          <textarea
            className="merchant-input merchant-textarea merchant-textarea--compact"
            value={form.store_point_of_view}
            onChange={(e) => updateField("store_point_of_view", e.target.value)}
            placeholder="Tell us briefly what your store is known for, who you dress, and why your range belongs in a curated style experience."
          />
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Do you have front-facing product images?</span>
          <select
            className="merchant-input"
            value={form.has_front_facing_images}
            onChange={(e) =>
              updateField("has_front_facing_images", e.target.value)
            }
          >
            <option value="">Select one</option>
            <option value="Yes, most products">Yes, most products</option>
            <option value="Some products">Some products</option>
            <option value="No / not sure">No / not sure</option>
          </select>
        </label>

        <label className="merchant-field">
          <span className="merchant-label">Open to early feedback?</span>
          <select
            className="merchant-input"
            value={form.open_to_early_feedback}
            onChange={(e) =>
              updateField("open_to_early_feedback", e.target.value)
            }
          >
            <option value="">Select one</option>
            <option value="Yes">Yes</option>
            <option value="Maybe">Maybe</option>
            <option value="No">No</option>
          </select>
        </label>

        {error ? <p className="merchant-error">{error}</p> : null}

        <div className="merchant-actions">
          <button className="gold-btn" type="submit" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit application"}
          </button>

          <Link className="public-navlink" to="/merchant/login">
            Already approved?
          </Link>
        </div>
      </form>
    </MerchantPageShell>
  );
}